import {
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  MapPin,
  Moon,
  RefreshCw,
  Sun,
  Wind,
  type LucideIcon,
} from "lucide-react";
import { useWeather } from "../../hooks/useWeather";

// Map a WMO weather code (open-meteo) to a label, an icon, and a background
// gradient. Day/night swaps the "clear" visuals.
function describeWeather(
  code: number,
  isDay: boolean,
): { label: string; Icon: LucideIcon; gradient: string } {
  const night = !isDay;
  if (code === 0)
    return night
      ? { label: "Clear night", Icon: Moon, gradient: "from-indigo-900 via-slate-900 to-black" }
      : { label: "Clear sky", Icon: Sun, gradient: "from-sky-400 via-cyan-500 to-blue-600" };
  if (code <= 3)
    return night
      ? { label: "Partly cloudy", Icon: Cloud, gradient: "from-slate-700 via-slate-800 to-slate-950" }
      : { label: "Partly cloudy", Icon: CloudSun, gradient: "from-sky-300 via-slate-400 to-slate-600" };
  if (code <= 48)
    return { label: "Fog", Icon: CloudFog, gradient: "from-slate-400 via-slate-500 to-slate-700" };
  if (code <= 57)
    return { label: "Drizzle", Icon: CloudDrizzle, gradient: "from-slate-500 via-slate-600 to-slate-800" };
  if (code <= 67)
    return { label: "Rain", Icon: CloudRain, gradient: "from-slate-600 via-blue-800 to-slate-900" };
  if (code <= 77)
    return { label: "Snow", Icon: CloudSnow, gradient: "from-sky-200 via-slate-300 to-slate-500" };
  if (code <= 82)
    return { label: "Rain showers", Icon: CloudRain, gradient: "from-slate-600 via-blue-800 to-slate-900" };
  if (code <= 86)
    return { label: "Snow showers", Icon: CloudSnow, gradient: "from-sky-200 via-slate-300 to-slate-500" };
  return {
    label: "Thunderstorm",
    Icon: CloudLightning,
    gradient: "from-slate-800 via-indigo-900 to-black",
  };
}

function formatToday(): { weekday: string; date: string } {
  const now = new Date();
  return {
    weekday: now.toLocaleDateString(undefined, { weekday: "long" }),
    date: now.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }),
  };
}

export function DashboardHero() {
  const { data, isLoading, error, refetch, isFetching } = useWeather();
  const { weekday, date } = formatToday();

  const weather = data ? describeWeather(data.weatherCode, data.isDay) : null;
  const gradient = weather?.gradient ?? "from-indigo-600 via-blue-700 to-slate-900";
  const Icon = weather?.Icon;

  return (
    <div
      className={`relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} p-6 text-white shadow-lg sm:p-8`}
    >
      {/* Decorative oversized glyph */}
      {Icon && (
        <Icon
          className="pointer-events-none absolute -right-6 -top-6 opacity-20"
          size={200}
          strokeWidth={1}
        />
      )}

      <div className="relative flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-[15px] font-medium uppercase tracking-widest text-white/70">
            {weekday}
          </p>
          <p className="mt-1 text-[40px] font-bold leading-none drop-shadow-sm sm:text-[52px]">
            {date}
          </p>

          <div className="mt-4 flex items-center gap-2 text-[15px] text-white/90">
            <MapPin size={16} />
            {isLoading ? (
              <span className="text-white/70">Finding your location…</span>
            ) : error ? (
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[13px] font-medium hover:bg-white/25"
              >
                <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} />
                Enable location for weather
              </button>
            ) : data ? (
              <span className="font-semibold">
                {data.place}
                {data.country ? <span className="font-normal text-white/70">, {data.country}</span> : null}
              </span>
            ) : null}
          </div>
        </div>

        {data && weather && (
          <div className="flex items-center gap-4">
            {Icon && <Icon size={64} strokeWidth={1.5} className="drop-shadow" />}
            <div className="leading-none">
              <p className="text-[56px] font-bold sm:text-[64px]">{Math.round(data.temperature)}°</p>
              <p className="mt-1 text-[15px] font-medium text-white/90">{weather.label}</p>
              <p className="mt-1 flex items-center gap-3 text-[13px] text-white/70">
                <span>Feels {Math.round(data.apparentTemperature)}°</span>
                <span className="inline-flex items-center gap-1">
                  <Wind size={12} />
                  {Math.round(data.windSpeed)} km/h
                </span>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
